import {useCallback, useEffect} from "react";
import {useSignal} from "@preact/signals-react";
import {postSubmit} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";
import {reloadBrandSkusTrigger} from "../@signals";

export default function useMatchersBrandSkus(brandID, excluded) {
	const brandOptions = useSignal([])
	const isLoading = useSignal(false)
	const lineID = useSignal(null)
	const searchText = useSignal('')

	// AbortController of the latest in-flight request; only the most recent request may mutate
	// state, so a late-resolving superseded/aborted request can't overwrite the new options.
	const controller = useSignal(null)

	useEffect(() => {
		return () => {
			if (controller.value) {
				controller.value.abort()
				controller.value = null
			}
		}
	}, [])

	const loadBrandSkus = useCallback(() => {
		if (!brandID) return

		if (controller.value) controller.value.abort()
		const myController = new AbortController()
		controller.value = myController

		isLoading.value = true
		postSubmit(`${BACKEND_APIS.MATCHERS}/selectors/skus-by-brand/${brandID}`, {
			lineID: lineID.value,
			find: searchText.value
		}, { signal: myController.signal }).then(res => {
			if (controller.value !== myController) return
			controller.value = null
			isLoading.value = false
			// skus-by-brand returns the SKU name as skuName, while this matcher (AI options included)
			// reads it as sku. Normalize here so every option has the same shape.
			brandOptions.value = res.options.map(({skuName, ...option}) => ({...option, sku: skuName}))
		}).catch(() => {
			if (controller.value !== myController) return
			controller.value = null
			isLoading.value = false
		})
	}, [brandID, lineID.value, searchText.value])

	// Clear options when the brand changes so the previous brand's SKUs don't linger.
	useEffect(() => {
		brandOptions.value = []
	}, [brandID])

	useEffect(() => {
		loadBrandSkus()
	}, [brandID, lineID.value, searchText.value, reloadBrandSkusTrigger.value]);

	const handleChangeSearchText = useCallback(newValue => {
		searchText.value = newValue
	}, [])

	const handleChangeLine = useCallback(newValue => {
		lineID.value = newValue
	}, [])

	return {
		brandOptions: brandOptions.value.filter(op => searchText.value ? true : !excluded.includes(op.skuID)),
		isLoading: isLoading.value,
		lineID: lineID.value,
		searchText: searchText.value,

		handleChangeLine,
		handleChangeSearchText
	}
}
